// src/hooks/useTpnCart.ts
import { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppSelector } from '../redux/hooks';
import { shouldShowCartIcon } from '../utils/tpnUtils';
import { checkIfRestaurantClosed, isInBreakHours } from '../utils/reservationUtils';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Custom hook for determining cart icon visibility based on TPN configuration
 * @param showCartIcon - Whether cart icon is enabled in widget settings
 * @returns boolean indicating if cart icon should be shown
 */
export const useTpnCart = (showCartIcon: boolean): boolean => {
  // Get TPN configuration from Redux store
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  // Get website config for cart settings from Site Content API
  const websiteConfig = useAppSelector((state) => state?.siteContent?.websiteConfig);
  
  // Get selected restaurant for working hours from Franchises API
  const selectedRestaurant = useAppSelector((state) => state?.restaurant?.selectedRestaurant);
  
  // Memoize the cart visibility logic with complete reservation checks
  const shouldShowCart = useMemo(() => {
    // Check 0: enabledCart (HIGHEST PRIORITY - from Site Content API)
    // Get cart enabled flag from websiteConfig.metadata
    const isAddToCartEnabled = websiteConfig?.metadata?.enabledCart ?? true;
    if (!isAddToCartEnabled) return false;
    
    // Parse working hours from Franchises API
    let workingHours = null;
    if (selectedRestaurant?.working_hours) {
      try {
        workingHours = typeof selectedRestaurant.working_hours === 'string'
          ? JSON.parse(selectedRestaurant.working_hours)
          : selectedRestaurant.working_hours;
      } catch (error) {
        console.error('Failed to parse working_hours:', error);
      }
    }
    
    // Check 1: Restaurant must be open today (use EST timezone)
    const todayDate = dayjs().tz('America/New_York').format('YYYY-MM-DD');
    const isRestaurantClosed = checkIfRestaurantClosed(todayDate, workingHours);
    if (isRestaurantClosed) return false;
    
    // Check 1.5: Hide cart during break hours (between lunch and dinner)
    if (isInBreakHours(workingHours)) return false;
    
    // Check 2: TPN Configuration must exist
    const hasTpnConfig = shouldShowCartIcon(showCartIcon, tpnConfig);
    if (!hasTpnConfig) return false;
    
    // All checks passed - show cart
    return true;
  }, [websiteConfig, selectedRestaurant, showCartIcon, tpnConfig]);
  
  return shouldShowCart;
};
